import { useEffect, useState, type ReactElement } from 'react';

/**
 * HONE — the global "Teach" modal. A dismissible textarea for freeform guidance the owner can open from
 * anywhere (a card's Teach button, or the chrome Teach button). On submit the host records the note; if
 * the note is too terse to act on the host may return a `followUpPrompt`, which this modal renders inline
 * and keeps open (still dismissible) so the owner can add context — never a blocking gate.
 */
export interface TeachModalProps {
  open: boolean;
  /** A short line describing what's being taught (the card title, or "the Decisions view"). */
  contextLabel?: string;
  /** Record the note. Return a follow-up prompt to ask for more context, or nothing to close. */
  onSubmit: (note: string) => Promise<{ followUpPrompt?: string } | void> | { followUpPrompt?: string } | void;
  onClose: () => void;
}

export function TeachModal({ open, contextLabel, onSubmit, onClose }: TeachModalProps): ReactElement | null {
  const [note, setNote] = useState('');
  const [followUp, setFollowUp] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset transient state whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setNote('');
      setFollowUp(null);
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (): Promise<void> => {
    if (!note.trim() || busy) return;
    setBusy(true);
    const res = await onSubmit(note.trim());
    setBusy(false);
    if (res && res.followUpPrompt) {
      setFollowUp(res.followUpPrompt);
      setNote('');
      return;
    }
    onClose();
  };

  return (
    <div className="lapo-dialog-backdrop" onMouseDown={onClose}>
      <div
        className="lapo-dialog lapo-teach-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Teach Lapo"
        data-testid="teach-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="lapo-dialog-title">Teach{contextLabel ? ` — ${contextLabel}` : ''}</h2>
        {followUp && (
          <p className="lapo-dialog-body" data-testid="teach-followup">
            {followUp}
          </p>
        )}
        <textarea
          className="lapo-teach-textarea"
          aria-label="What should Lapo do differently?"
          data-testid="teach-input"
          placeholder="Tell Lapo what to do differently next time…"
          value={note}
          autoFocus
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="lapo-dialog-actions">
          <button type="button" className="lapo-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="lapo-btn is-primary"
            data-testid="teach-send"
            disabled={!note.trim() || busy}
            onClick={() => void submit()}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
